/**
 * Local platform logos served from `public/osint-logo`.
 * OSINT result pages prefer these over Simple Icons.
 */

/** Filenames in `public/osint-logo`. The folder is not scanned at runtime. */
const OSINT_LOGO_FILENAMES = [
  'AJIO.jpeg',
  'About.me.jpeg',
  'Academia.jpeg',
  'AdvanceCaller.jpeg',
  'Amazon.png',
  'Audiomack.png',
  'Bandcamp.png',
  'Bluesky.png',
  'Botim.jpeg',
  'CallApp.png',
  'CallSpamChecker.jpeg',
  'CaringBridge.png',
  'Chess.png',
  'Codeberg.png',
  'Codewars.jpeg',
  'D2H.jpeg',
  'Dailymotion.png',
  'Dev.png',
  'DeviantArt.png',
  'Discogs.png',
  'DishTV.png',
  'Disqus.png',
  'Docker Hub.png',
  'Dribbble.png',
  'Duolingo.jpeg',
  'Eyecon.jpeg',
  'Facebook.png',
  'Flickr.png',
  'Flipboard.png',
  'Flipkart.png',
  'Foursquare.png',
  'GPay.jpeg',
  'Genius.png',
  'GetContact.png',
  'GitHub.png',
  'GitLab.png',
  'Gronda.jpeg',
  'Gumroad.png',
  'Hashnode.jpeg',
  'Imgur.jpeg',
  'Instagram.jpeg',
  'Keybase.png',
  'Kick.png',
  'Launchpad.jpeg',
  'Letterboxd.png',
  'Lichess.png',
  'Life360.jpeg',
  'LinkedIn.png',
  'Linktree.png',
  'LiveJournal.png',
  'Mastodon.jpeg',
  'Medium.jpeg',
  'Microsoft.png',
  'Mixcloud.png',
  'MyAnimeList.png',
  'Peloton.png',
  'Picsart.jpeg',
  'Pinterest.png',
  'Polymarket.jpeg',
  'Poshmark.png',
  'Quora.png',
  'Reddit.jpeg',
  'RedorSearchApp.jpeg',
  'Replit.png',
  'Roblox.png',
  'Rumble.jpeg',
  'Showcaller.jpeg',
  'Simpler.me.png',
  'Simpler.png',
  'Snapchat.jpeg',
  'SoundCloud.png',
  'SourceForge.png',
  'Speaker Deck.png',
  'Spotify.png',
  'Steam.jpeg',
  'Strava.png',
  'Substack.png',
  'Sync.ME.jpeg',
  'Tata Play.png',
  'Teams.jpeg',
  'Telegram.jpeg',
  'Threads.jpeg',
  'TikTok.png',
  'TrueCallApp.png',
  'Truecaller ID.jpeg',
  'Truecaller.jpeg',
  'Tumblr.jpeg',
  'UPI.png',
  'Unacademy.png',
  'Untappd.png',
  'VSCO.png',
  'Vimeo.png',
  'Wanderlog.png',
  'WhatsApp.jpeg',
  'Wikidot.jpeg',
  'Wikipedia.png',
  'X (Twitter).png',
  'Yango.jpeg',
  'YouTube.png',
  'npm.png',
  'plex.png',
];

/** Names that do not match the filename after stripping spaces and punctuation. */
const OSINT_LOGO_ALIASES: Record<string, string> = {
  fb: 'Facebook.png',
  googlepay: 'GPay.jpeg',
  getcontacts: 'GetContact.png',
  simpleme: 'Simpler.me.png',
  microsoftteams: 'Teams.jpeg',
  academiaedu: 'Academia.jpeg',
  redorsearch: 'RedorSearchApp.jpeg',
  x: 'X (Twitter).png',
  twitter: 'X (Twitter).png',
  devto: 'Dev.png',
  docker: 'Docker Hub.png',
};

function filenameLookupKey(filename: string): string {
  return filename
    .replace(/\.(png|jpe?g|webp|gif)$/i, '')
    .toLowerCase()
    .replace(/\.com$/i, '')
    .replace(/[^a-z0-9]+/g, '');
}

function buildOsintLogoFiles(): Record<string, string> {
  const files: Record<string, string> = {};
  for (const filename of OSINT_LOGO_FILENAMES) {
    files[filenameLookupKey(filename)] = filename;
  }
  return { ...files, ...OSINT_LOGO_ALIASES };
}

const OSINT_LOGO_FILES = buildOsintLogoFiles();

const LOGO_KEYS_BY_LENGTH = Object.keys(OSINT_LOGO_FILES).sort(
  (a, b) => b.length - a.length,
);

function normalizePlatformLookupKey(platform: string | null | undefined): string {
  return String(platform || '')
    .trim()
    .replace(/[_\s-]+\d+$/g, '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/\.com$/i, '')
    .replace(/[^a-z0-9]+/g, '');
}

function logoUrl(filename: string): string {
  return `osint-logo/${encodeURIComponent(filename)}`;
}

/** Public URL for a platform logo in `public/osint-logo`, or null when none exists. */
export function resolveOsintPlatformLogoUrl(
  platform: string | null | undefined,
): string | null {
  const key = normalizePlatformLookupKey(platform);
  if (!key) {
    return null;
  }

  const exact = OSINT_LOGO_FILES[key];
  if (exact) {
    return logoUrl(exact);
  }

  for (const candidate of LOGO_KEYS_BY_LENGTH) {
    if (candidate.length < 5) {
      continue;
    }
    if (key.includes(candidate)) {
      return logoUrl(OSINT_LOGO_FILES[candidate]);
    }
  }

  return null;
}
